import {FC, HTMLProps, useCallback, useEffect, useMemo} from 'react';
import {useField} from 'formik';
import {Form} from '@components/Form';
import {InputGroup} from '@components/InputGroup';
import {FormControlProps} from 'react-bootstrap';
import {getCountries, getCountryCallingCode} from 'libphonenumber-js';

type Props = {
    className?: string;
    label?: string;
    placeholder?: string;
    required?: boolean;
} & ({label: string} | {placeholder: string}) &
    FormControlProps &
    HTMLProps<HTMLInputElement>;

export type PhoneInputProps = Props & {name: string};

const prefixes = getCountries().map(c => '+' + getCountryCallingCode(c)).filter((v, i, a) => a.indexOf(v) === i).sort();
const preferredPrefixes = [
    '+420',
    '+421',
];

export const PhoneInput: FC<PhoneInputProps> = ({className, label, placeholder, required, name, ...innerProps}) => {
    const [field, meta, helpers] = useField(name);
    const {value, onChange, name: _, ...restField} = field;
    const setFieldValue = helpers.setValue;
    const setTouched = helpers.setTouched;

    const isTouched = meta.touched;
    const ownErrors = meta.error;
    const hasErrors = !!ownErrors;

    const prefix = useMemo(() => (value.length >= 4 ? value.substring(0, 4) : '+420'), [value]);
    const number = useMemo(() => value.substring(4), [value]);

    useEffect(() => {
        if (number.startsWith(prefix)) {
            setFieldValue(number);
        }
    }, [setFieldValue, number, prefix]);

    const onPrefixChange = useCallback(
        (e) => {
            setFieldValue(e.target.value + number);
            setTouched(true);
        },
        [setFieldValue, setTouched, number],
    );

    const onValueChange = useCallback(
        (e) => {
            setFieldValue(prefix + e.target.value);
            setTouched(true);
        },
        [setFieldValue, setTouched, prefix],
    );

    const preferred = useMemo(() => prefixes.filter(p => preferredPrefixes.includes(p)), []);
    const other = useMemo(() => prefixes.filter(p => !preferredPrefixes.includes(p)), []);

    return (
        <Form.Group className={className}>
            <Form.Label visuallyHidden={!label} htmlFor={name + '-number'}>
                {label ?? placeholder}
            </Form.Label>
            <InputGroup hasValidation={isTouched && hasErrors}>
                <InputGroup.Text>
                    <Form.Select
                        id={name + '-prefix'}
                        name={name + '-prefix'}
                        autoComplete={'tel-country-code'}
                        value={prefix}
                        onChange={onPrefixChange}
                    >
                        <optgroup>
                            {preferred.map(p => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </optgroup>
                        <optgroup>
                            {other.map(p => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </optgroup>
                    </Form.Select>
                </InputGroup.Text>
                <Form.Control
                    placeholder={placeholder}
                    id={name + '-number'}
                    name={name + '-number'}
                    value={number}
                    autoComplete={'tel-national'}
                    {...restField}
                    {...innerProps}
                    isInvalid={isTouched && hasErrors}
                    onChange={onValueChange}
                />
                {required && <InputGroup.Required>*</InputGroup.Required>}
                {isTouched && hasErrors && <Form.Control.Feedback type={'invalid'}>{ownErrors}</Form.Control.Feedback>}
            </InputGroup>
        </Form.Group>
    );
};
